'use client';

import { useCallback, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  useGetAccountLockQuery,
  useGetCanManageBillingPermissionQuery,
} from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useAccountLockDialogContext } from '@frameio/shared/contexts/ModalContext/ModalContext';
import { AccountLockingDialog } from './components/AccountLockingDialog';
import { PLAN_URL } from './constants';
import { getFirstPartOfPathname } from './utils';

export const AccountLock = ({
  forcedLock = false,
}: {
  forcedLock?: boolean;
}) => {
  const pathname = usePathname();
  const router = useRouter();
  const accountId = useActiveAccountId();
  const isWebClient = useIsMatchingClientName('web');

  const {
    isAccountLockDialogOpen,
    openAccountLockDialog,
    closeAccountLockDialog,
  } = useAccountLockDialogContext();

  const { data } = useGetAccountLockQuery({
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
  });

  const { loading: isPermissionsLoading } =
    useGetCanManageBillingPermissionQuery({
      variables: { accountId: accountId ?? '' },
      skip: !accountId,
    });

  const account = data?.account;

  const isAccountLocked = !!account?.lockedAt || forcedLock;
  const isPausedPlan = account?.lockReason === 'PLAN_PAUSED';

  const firstPartOfPathname = getFirstPartOfPathname(pathname);
  const isSettingsRoute = firstPartOfPathname === 'settings';
  const isSharesRoute = firstPartOfPathname === 'share';

  /**
   * Re-routes the user to the settings page if
   * the route is not settings and not a share
   */
  useEffect(() => {
    if (!isAccountLocked) return;

    const shouldNotReroute = isSharesRoute || isSettingsRoute;

    if (
      (shouldNotReroute && !isPermissionsLoading) ||
      forcedLock ||
      !isWebClient
    ) {
      /** The banner is sufficient to inform the user their account is paused */
      if (isPausedPlan) return;
      openAccountLockDialog();
    } else {
      router.push(PLAN_URL);
    }
  }, [
    forcedLock,
    isAccountLocked,
    isPausedPlan,
    isPermissionsLoading,
    isSettingsRoute,
    isSharesRoute,
    isWebClient,
    openAccountLockDialog,
    router,
  ]);

  const handleOnClose = useCallback(() => {
    closeAccountLockDialog();
  }, [closeAccountLockDialog]);

  if (!isAccountLocked) {
    return null;
  }

  const isAccountLockingDialogShowing = isAccountLockDialogOpen || forcedLock;
  return (
    <AccountLockingDialog
      isForcedLock={forcedLock}
      isShare={isSharesRoute}
      isShowing={isAccountLockingDialogShowing}
      onClose={handleOnClose}
    />
  );
};
